import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Button, TextField } from "@legends/ui";
import { Room, RoomEvent, Track } from "livekit-client";
import { guestBoard, guestJoin, type DuelSlot, type ProgramState, type WhiteboardStroke } from "./api";
import { ConnectionBanner, useBackgroundMediaSuspend, useConnState } from "./liveConnection";
import { useTalkbackListener } from "./talkback";
import { useTalentAudio } from "./talentAudio";
import { useDirectorOnAirAudio } from "./directorMic";
import { resolveBoardImageUrl, useBoardSlates } from "./boardSlates";
import { alertStyle, monoLabelStyle, LegendBrandMark } from "./sections";
import WhiteboardCanvas from "./Whiteboard";
import ProgramComposition from "./ProgramComposition";

// Public guest-publisher surface (ADR 0044). A NON-operator opens a shared
// /guest/<code> link, enters a display name, grants camera/mic, and publishes
// into ONE broadcast's LiveKit room. No AuthGate, no operator chrome: the guest
// holds only the room-scoped LiveKit token the join endpoint mints — no cave,
// tenant, or app authority. The Director decides what viewers actually see; the
// guest just knows they're live. The connect+publish flow mirrors the legend
// Go-Live path, and the reconnect banner is the shared ADR 0042 affordance.

const videoStyle: CSSProperties = {
  width: "100%",
  aspectRatio: "16 / 9",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  objectFit: "cover",
};

const monoValueStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-2xs)",
  letterSpacing: "var(--ls-label)",
  color: "var(--text-dim)",
};

// Keep-foregrounded guidance (LSC-639). Mobile browsers may pause the camera when the
// publishing tab is backgrounded or the device locks (and ADR 0227 deliberately
// suspends a hidden guest's publish), so a guest who switches apps mid-show goes black.
// This is instruction copy only — it promises no background capture and changes no
// camera/permission/audio behaviour. Shown in full before joining; a compact,
// expandable reminder while connected so it never crowds the camera or controls.
const KEEP_FOREGROUND_TIPS = [
  "Keep this Guest tab open and visible.",
  "Don't switch tabs, minimize the browser, or switch to another app.",
  "Don't lock your phone or let the screen go to sleep.",
  "If your picture stops, return to this tab. If it doesn't recover, tell your Director before refreshing or rejoining.",
];
const KEEP_FOREGROUND_WHY = "Your camera may pause when this page is in the background or your device locks.";

function KeepForegroundTips({ compact }: { compact: boolean }) {
  const list = (
    <>
      <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4, fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}>
        {KEEP_FOREGROUND_TIPS.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-dim)" }}>{KEEP_FOREGROUND_WHY}</p>
    </>
  );
  const box: CSSProperties = {
    display: "grid",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid var(--border)",
    borderLeft: "3px solid var(--gold)",
    background: "var(--surface-1, rgba(0,0,0,0.2))",
  };
  if (compact) {
    return (
      <details aria-label="Camera tips" style={box}>
        <summary style={{ cursor: "pointer", ...monoValueStyle, color: "var(--gold)" }}>
          Keep this tab open &amp; your screen awake
        </summary>
        {list}
      </details>
    );
  }
  return (
    <section aria-label="Camera tips" style={box}>
      <h2 style={{ ...monoValueStyle, margin: 0, color: "var(--gold)" }}>While your camera is connected</h2>
      {list}
    </section>
  );
}

export interface GuestJoinScreenProps {
  code: string;
}

export default function GuestJoinScreen({ code }: GuestJoinScreenProps) {
  const [displayName, setDisplayName] = useState("");
  const [live, setLive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // Self-view visibility once connected (LSC-561): before connect the camera self-preview
  // is the main view; after connect the PROGRAM composition (the Fan mix) is the main view
  // and the self-view is a toggle-able PiP, hidden by default.
  const [showSelf, setShowSelf] = useState(false);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The director's program state (ADR 0022) — enough for the guest to see when
  // the show is on standby or ended. The guest never sees operator controls.
  const [program, setProgram] = useState<ProgramState>({});
  // The guest's STABLE program key (participant Name), returned by the join
  // endpoint — the guest can't derive it from its unique identity (ADR 0056). It
  // resolves which dueling slot (if any) the Director has put the guest on.
  const [stableKey, setStableKey] = useState<string | null>(null);
  // The connected Room, held in state so the talkback listener (ADR 0213) wires up
  // once the guest joins.
  const [roomState, setRoomState] = useState<Room | null>(null);
  const [conn, setConn] = useConnState();
  // Director talkback (ADR 0213): play the Director's private return-audio + show an
  // indicator when the Director is addressing this guest (or all talent). Keyed by
  // the guest's stable key (ADR 0042/0056).
  const { talkbackActive, talkbackAudioRef } = useTalkbackListener(roomState, stableKey ?? undefined);
  // Talent conversation audio (LSC-530): hear the Legend and the other guests,
  // independent of what the Director has on Program. Fans/Director-talkback excluded.
  const { containerRef: talentAudioRef } = useTalentAudio(roomState);
  // Director mic → On Air (LSC-633, ADR 0247): hear the Director's PUBLIC on-air voice
  // (the fan-mix plane), separate from the private talkback return path above.
  const { containerRef: directorMicRef } = useDirectorOnAirAudio(roomState);
  // Background media suspend (ADR 0227, LSC-506): when the guest backgrounds the tab
  // / locks the phone / navigates away, stop publishing BOTH mic and camera (a
  // backgrounded tab's video stops on its own, but the mic keeps capturing — so a
  // guest who thinks they've left is still heard). Restored to the prior enabled
  // state on return. No-op until the guest is live (roomState is null before then).
  useBackgroundMediaSuspend(roomState);
  // The board-slate catalog (ADR 0058) — the guest's dueling-slot board may be an
  // admin-uploaded image slate the Director set; the guest draws on it either way.
  const boardSlates = useBoardSlates();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const roomRef = useRef<Room | null>(null);
  const mountedRef = useRef(true);
  const boardTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const attachStream = (stream: MediaStream | null) => {
    if (!videoRef.current) return;
    try {
      videoRef.current.srcObject = stream;
    } catch {
      // jsdom / older browsers without srcObject — the preview is cosmetic.
    }
  };

  const stopPreview = useCallback(() => {
    const s = streamRef.current;
    streamRef.current = null;
    if (s) s.getTracks().forEach((t) => t.stop());
    attachStream(null);
  }, []);

  // Prime a camera/mic preview so the guest sees themselves before going live.
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (!mountedRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        attachStream(stream);
      } catch (err: unknown) {
        if (mountedRef.current) setError(deviceErr(err));
      }
    })();
    return () => {
      mountedRef.current = false;
      stopPreview();
      const room = roomRef.current;
      roomRef.current = null;
      if (room) void room.disconnect();
    };
  }, [stopPreview]);

  const join = async () => {
    if (connecting || live) return;
    setError(null);
    setConnecting(true);
    setConn("live");
    try {
      const t = await guestJoin(code, displayName.trim() || undefined);
      // Re-sync the program state on (re)join so a reconnecting/refreshing guest's
      // dueling board reflects the current state (its slot + strokes) instead of a
      // stale/blank one until the next relay (ADR 0062). Later data messages update it.
      if (t.program && mountedRef.current) setProgram(t.program);
      const room = new Room();
      roomRef.current = room;
      room.on(RoomEvent.Disconnected, () => {
        if (mountedRef.current && roomRef.current === room) {
          roomRef.current = null;
          setLive(false);
          setRoomState(null); // tear down the talkback listener (ADR 0213)
          setConn("lost");
        }
      });
      room.on(RoomEvent.Reconnecting, () => {
        if (mountedRef.current) setConn("reconnecting");
      });
      room.on(RoomEvent.Reconnected, () => {
        if (mountedRef.current) setConn("reconnected");
      });
      // Track the director's program state so a guest sees standby / ended.
      room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        try {
          const parsed = JSON.parse(new TextDecoder().decode(payload)) as {
            type?: string;
            state?: ProgramState;
          };
          if (parsed.type === "program" && parsed.state && mountedRef.current) setProgram(parsed.state);
        } catch {
          // ignore non-program / malformed data messages
        }
      });
      await room.connect(t.url, t.token);
      // Release the raw preview so LiveKit can claim the same camera device.
      stopPreview();
      await room.localParticipant.setCameraEnabled(true);
      await room.localParticipant.setMicrophoneEnabled(true);
      const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const videoTrack = pub?.videoTrack;
      if (videoRef.current && videoTrack) videoTrack.attach(videoRef.current);
      if (!mountedRef.current) return;
      setRoomName(t.room);
      setStableKey(t.name ?? null);
      setRoomState(room); // wire the talkback listener (ADR 0213)
      setLive(true);
    } catch (err: unknown) {
      const room = roomRef.current;
      roomRef.current = null;
      if (room) void room.disconnect();
      if (mountedRef.current) {
        setError(msg(err));
        // Fall back to the pre-live preview so the guest can retry.
        void reacquirePreview();
      }
    } finally {
      if (mountedRef.current) setConnecting(false);
    }
  };

  const reacquirePreview = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      attachStream(stream);
    } catch {
      // Preview is cosmetic; a failure here never blocks a retry.
    }
  }, []);

  const rejoin = () => {
    setLive(false);
    void join();
  };

  const status = program.ended
    ? { label: "BROADCAST ENDED", color: "var(--text-muted)" }
    : program.standby
      ? { label: "ON STANDBY", color: "var(--ember)" }
      : live
        ? { label: "YOU ARE LIVE", color: "var(--alert-2)" }
        : null;

  // Guest telestration (ADR 0056). In the `dueling` layout (ADR 0054) each of the
  // two heads draws only its OWN board, keyed by slot: `source` = slot A, `sourceB`
  // = slot B (matched by the guest's STABLE key). A guest holds a media-only token
  // (no data-publish, ADR 0044), so it can't relay strokes over LiveKit — instead
  // it POSTs them to the server, which merges them into ONLY this slot and fans
  // them out. The guest draws only when live + on a slot + not on standby/ended.
  const myDuelSlot: DuelSlot | null =
    live && !program.standby && !program.ended && stableKey && program.layout === "dueling"
      ? program.source === stableKey
        ? "a"
        : program.sourceB === stableKey
          ? "b"
          : null
      : null;
  const duelBoard = myDuelSlot ? program.boards?.[myDuelSlot] : undefined;

  // Draw handler: optimistically reflect the stroke locally (so it appears
  // instantly), then debounce a POST to the server relay. The board TEMPLATE is
  // the Director's (read-only here) — the guest owns only its strokes.
  const drawDuel = (next: WhiteboardStroke[]) => {
    if (!myDuelSlot) return;
    const boards = { ...(program.boards ?? {}) };
    boards[myDuelSlot] = { ...(boards[myDuelSlot] ?? {}), strokes: next };
    setProgram({ ...program, boards });
    if (boardTimer.current) clearTimeout(boardTimer.current);
    boardTimer.current = setTimeout(() => {
      guestBoard(code, next).catch((err: unknown) => {
        if (mountedRef.current) setError(msg(err));
      });
    }, 250);
  };

  // Never fire a queued board POST after unmount.
  useEffect(
    () => () => {
      if (boardTimer.current) clearTimeout(boardTimer.current);
    },
    [],
  );

  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{
        minHeight: "calc(100vh / var(--ui-zoom, 1))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      {/* Director talkback (ADR 0213): the private return-audio plays here (hidden),
          with an indicator so the guest knows the Director is in their ear. */}
      <audio ref={talkbackAudioRef} autoPlay aria-hidden />
      {/* Talent conversation audio (LSC-530): the other talent's mics play here
          (hidden), independent of Program. */}
      <div ref={talentAudioRef} aria-hidden style={{ display: "none" }} />
      {/* Director mic → On Air (ADR 0247): the Director's public on-air voice plays here
          (hidden) when they're On Air — the fan-mix plane, separate from private talkback. */}
      <div ref={directorMicRef} aria-hidden style={{ display: "none" }} />
      {talkbackActive && (
        <div role="status" aria-label="Director talkback active" style={{ position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 50, display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 999, background: "var(--live, #e5484d)", color: "#fff", fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", boxShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />
          Director talkback
        </div>
      )}
      <div
        aria-label="Guest join"
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "22px 26px 24px",
          width: 460,
          maxWidth: "100%",
          display: "grid",
          gap: 14,
          animation: "cnPop var(--dur) var(--ease-out)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <LegendBrandMark />
          <h1 style={{ ...monoLabelStyle, margin: 0, color: "var(--text-hi)" }}>Join as a guest</h1>
          {status && (
            <span
              role="status"
              style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: "var(--fs-2xs)", letterSpacing: "var(--ls-caps)", color: status.color }}
            >
              {status.label}
            </span>
          )}
        </div>

        <ConnectionBanner state={conn} onRejoin={rejoin} />

        {/* Monitor (LSC-561): before connect the camera self-preview is the main view;
            once live, the PROGRAM composition — the same visual Fan mix the Director
            controls — is the main view (VIDEO-ONLY; guests hear each other on the
            talent-conversation plane, ADR 0218), with the self-preview a toggle-able PiP.
            The self <video> is ALWAYS mounted (it owns the camera track). */}
        {live ? (
          <div style={{ position: "relative" }}>
            <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: "#000", borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border-mute)" }}>
              <ProgramComposition room={roomState} program={program} boardSlates={boardSlates} ariaLabel="Program monitor — what viewers see" />
            </div>
            <div
              style={
                showSelf
                  ? { position: "absolute", right: 8, top: 8, width: "36%", minWidth: 110, aspectRatio: "16 / 9", zIndex: 5, borderRadius: "var(--radius-sm)", overflow: "hidden", border: "2px solid var(--gold)", boxShadow: "var(--shadow-pop)", background: "#000" }
                  : { position: "absolute", left: -99999, top: 0, width: 160, height: 90, opacity: 0, pointerEvents: "none", overflow: "hidden" }
              }
            >
              <video ref={videoRef} muted autoPlay playsInline aria-label="Your camera preview" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <Button size="sm" variant="muted" aria-label={showSelf ? "Hide self-view" : "Show self-view"} aria-pressed={showSelf} onClick={() => setShowSelf((v) => !v)}>
                {showSelf ? "Hide self-view" : "Show self-view"}
              </Button>
            </div>
          </div>
        ) : (
          <video ref={videoRef} muted autoPlay playsInline aria-label="Camera preview" style={videoStyle} />
        )}

        {live ? (
          <div style={{ display: "grid", gap: 10 }}>
            <p style={{ margin: 0, fontSize: "var(--fs-body)", color: "var(--text-hi)" }}>
              You're publishing to the show. The director controls what viewers see — you may be on program, on
              standby, or off air at any moment.
            </p>
            <KeepForegroundTips compact />
            {/* Your own dueling board (ADR 0056) — shown only when the Director has
                put you on a dueling slot. You draw; the Director set the board
                template. Strokes relay to viewers through the server. */}
            {myDuelSlot && (
              <section aria-label="Your board" style={{ display: "grid", gap: 6 }}>
                <span style={{ ...monoValueStyle, color: "var(--gold)" }}>
                  Your board — slot {myDuelSlot.toUpperCase()} (draw a play)
                </span>
                <WhiteboardCanvas
                  strokes={duelBoard?.strokes ?? []}
                  board={duelBoard?.board}
                  boardImageUrl={resolveBoardImageUrl(duelBoard?.board, boardSlates)}
                  editable
                  onChange={drawDuel}
                  color="#c9a227"
                  ariaLabel="Your board editor"
                />
              </section>
            )}
            {roomName && <span style={monoValueStyle}>ROOM {roomName}</span>}
          </div>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-dim)" }}>
              Enter your name and go live. You'll join this one broadcast only.
            </p>
            <KeepForegroundTips compact={false} />
            <TextField
              label="Display name"
              name="displayName"
              value={displayName}
              onChange={setDisplayName}
              onSubmit={() => void join()}
              full
            />
            <div>
              <Button variant="accent" aria-label="Go live" disabled={connecting} onClick={() => void join()}>
                {connecting ? "Connecting…" : "Go live"}
              </Button>
            </div>
          </>
        )}

        {error && (
          <p role="alert" style={alertStyle}>
            {error}
          </p>
        )}
      </div>
    </main>
  );
}

function msg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** getUserMedia rejections carry a DOMException name we can turn into guidance. */
function deviceErr(err: unknown): string {
  const name = err instanceof DOMException ? err.name : err instanceof Error ? err.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Camera and microphone access was denied.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No camera or microphone was found.";
  }
  return msg(err);
}
